import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Modal, Form, Input, InputNumber, DatePicker, Select, Space, Tag, Popconfirm, Typography, notification } from 'antd';
import { MinusCircleOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../authentication/AuthProvider';
import request from '../lib/request';
import { PdfButton, EmailButton } from './DocumentActions';

export const CREDIT_NOTE_STATUS_COLORS = { DRAFT: 'default', ISSUED: 'green', CANCELLED: 'red' };
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The credit notes raised against one invoice, plus "Raise credit note" (a partial or full credit, issued in one step). */
export default function CreditNotesCard({ invoice, onChanged }) {
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const [rows, setRows] = React.useState([]);
  const [vatCodes, setVatCodes] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [form] = Form.useForm();

  const fetchRows = React.useCallback(() => {
    request
      .get('/credit-notes', { invoice_id: invoice.id })
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data));
  }, [invoice.id]);
  React.useEffect(fetchRows, [fetchRows]);

  React.useEffect(() => {
    request
      .get('/vat-setup/vat-codes', { company_id: invoice.company_id })
      .then((r) => r.json())
      .then((r) => r.status && setVatCodes(r.data));
  }, [invoice.company_id]);

  const creditable = Number(invoice.total) - Number(invoice.amount_credited || 0);
  const canRaise = isManager && ['ISSUED', 'PARTIALLY_PAID', 'PAID'].includes(invoice.status) && creditable > 0.005;

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({
      credit_date: moment(),
      lines: (invoice.InvoiceLines || []).map((l) => ({ description: l.description, quantity: Number(l.quantity), unit_price: Number(l.unit_price), vat_code_id: l.vat_code_id, vat_rate: Number(l.vat_rate) })),
    });
    setOpen(true);
  };

  const handleIssue = () => {
    form.validateFields().then((values) => {
      setBusy(true);
      request
        .post('/credit-notes', { invoice_id: invoice.id, credit_date: values.credit_date.format('YYYY-MM-DD'), reason: values.reason, notes: values.notes, lines: values.lines, issue: true })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: `${r.data.credit_note_number} issued` });
            setOpen(false);
            fetchRows();
            onChanged();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setBusy(false));
    });
  };

  const cancel = (note) => {
    request
      .post(`/credit-notes/${note.id}/cancel`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `${note.credit_note_number} cancelled` });
          fetchRows();
          onChanged();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  return (
    <Card
      title={`Credit Notes (${rows.filter((r) => r.status === 'ISSUED').length})`}
      size="small"
      style={{ marginTop: 16 }}
      extra={
        canRaise && (
          <Button size="small" type="primary" ghost icon={<PlusOutlined />} onClick={openModal}>
            Raise credit note
          </Button>
        )
      }
    >
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'No credit notes against this invoice' }}
        columns={[
          { title: 'Number', dataIndex: 'credit_note_number', render: (v, c) => <Link to={`/credit-notes?highlight=${c.id}`}>{v}</Link> },
          { title: 'Date', dataIndex: 'credit_date' },
          { title: 'Reason', dataIndex: 'reason' },
          { title: 'Total', dataIndex: 'total', align: 'right', render: money },
          { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={CREDIT_NOTE_STATUS_COLORS[v]}>{v}</Tag> },
          {
            title: '',
            key: 'actions',
            render: (_, c) => (
              <Space size={4}>
                <PdfButton type="credit-note" id={c.id} size="small" children={null} />
                {c.status === 'ISSUED' && <EmailButton type="credit-note" id={c.id} size="small" children={null} />}
                {c.status === 'ISSUED' && isManager && (
                  <Popconfirm title="Cancel this credit note? The invoice balance is restored." onConfirm={() => cancel(c)}>
                    <Button size="small" danger icon={<StopOutlined />} title="Cancel" />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal title={`Raise credit note against ${invoice.invoice_number}`} open={open} onCancel={() => setOpen(false)} onOk={handleIssue} okText="Issue credit note" confirmLoading={busy} width={860}>
        <Typography.Paragraph type="secondary">
          Up to EUR {money(creditable)} can still be credited on this invoice. Remove or reduce lines for a partial credit. Issuing reduces the invoice balance and reverses the
          output VAT in the ledger.
        </Typography.Paragraph>
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="credit_date" label="Credit note date" rules={[{ required: true }]} style={{ width: 180 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reason" label="Reason" rules={[{ required: true }]} style={{ width: 600 }}>
              <Input maxLength={255} placeholder="e.g. Service not performed in September; fee agreed at a lower amount" />
            </Form.Item>
          </Space>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field) => (
                  <Space key={field.key} style={{ display: 'flex' }} align="start">
                    <Form.Item {...field} name={[field.name, 'description']} rules={[{ required: true }]} style={{ width: 330 }}>
                      <Input placeholder="Description" maxLength={255} />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'quantity']} rules={[{ required: true }]} style={{ width: 80 }}>
                      <InputNumber min={0.01} placeholder="Qty" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'unit_price']} rules={[{ required: true }]} style={{ width: 120 }}>
                      <InputNumber min={0} precision={2} placeholder="Unit price" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'vat_code_id']} style={{ width: 180 }}>
                      <Select
                        allowClear
                        placeholder="VAT code"
                        options={vatCodes.map((v) => ({ value: v.id, label: `${v.code} - ${v.rate}%` }))}
                        onChange={(id) => {
                          const code = vatCodes.find((v) => v.id === id);
                          const lines = form.getFieldValue('lines');
                          lines[field.name].vat_rate = code ? Number(code.rate) : 0;
                          form.setFieldsValue({ lines });
                        }}
                      />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'vat_rate']} style={{ width: 80 }}>
                      <InputNumber min={0} max={100} placeholder="%" style={{ width: '100%' }} />
                    </Form.Item>
                    <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1, unit_price: 0, vat_rate: 0 })}>
                  Add line
                </Button>
              </div>
            )}
          </Form.List>
          <Form.Item name="notes" label="Notes on the credit note" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
